import { Grid } from '@mui/material'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { useNotification } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { getUserStoreInfo } from 'utils/authService'
import { handleQueryError } from 'utils/helpers'
import { getInitialValues, getTotals, getValidationSchema } from './helpers'
import { toAccompanyingNotePayload } from './serializers'
import {
  getAccompanyingNote,
  getCollectingCompanyOptions,
  getWasteReportCollectionCompanyOptions
} from 'components/waste-management/api'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Alert, Autocomplete, Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import ls from './AccompanyingNoteModal.modules.scss'
import {
  Delegate,
  DelegateOptionType,
  WasteCollectionCompanyDeliveryAddress,
  WasteCollectionCompanyDeliveryAddressType
} from 'components/waste-management/interfaces'
import { set } from 'immer/dist/internal'
import { useSelectSearch } from 'hooks/useSelectSearch'

const AccompanyingNoteModal = props => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefetchItem, setShouldRefreshData } =
    props

  const [currentStore, setCurrentStore] = useState(null)
  const [totalWeight, setTotalWeight] = useState<number>(0)

  useEffect(() => {
    setCurrentStore(getUserStoreInfo())
  }, [])

  const { showNotification } = useNotification()

  const { data: editItem } = getAccompanyingNote(showNotification, editItemId, shouldRefetchItem)

  const { data: companyOptions } = getCollectingCompanyOptions(showNotification)

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem, companyOptions])

  const validationSchema = useMemo(() => getValidationSchema(), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toAccompanyingNotePayload(values))
    }
  })

  const { data: wasteReportsOptions } = getWasteReportCollectionCompanyOptions(
    showNotification,
    +formik.values.company?.value,
    isEdit
  )

  useEffect(() => {
    if (wasteReportsOptions) {
      if (!isEdit) {
        formik.setFieldValue('wasteReports', wasteReportsOptions)
      }
    }
  }, [wasteReportsOptions])

  useEffect(() => {
    if (isEdit && editItem) {
      setTotalWeight(editItem.measuredWeight)
    } else if (formik.values.wasteReports?.length) {
      const computedTotalWeight = getTotals(formik.values.wasteReports)
      setTotalWeight(computedTotalWeight)
    } else {
      setTotalWeight(0)
    }
  }, [formik.values.wasteReports])

  const delegateSelectSearch = useSelectSearch<Delegate, DelegateOptionType>({
    url: '/waste-management-system/delegate',
    filterName: 'name',
    optionsMap: item => ({
      ...item,
      label: item?.name,
      value: item?.cnp
    }),
    initialSearchText: formik.values?.delegateName ?? ''
  })

  const wasteCollectionCompanyDeliveryAddressSelectSearch = useSelectSearch<
    WasteCollectionCompanyDeliveryAddress,
    WasteCollectionCompanyDeliveryAddressType
  >({
    url: '/waste-management-system/waste-accompanying-note/delivery-addresses',
    filterName: 'address',
    optionsMap: item => ({
      ...item,
      label: item?.address,
      value: item?.id
    }),
    initialSearchText: formik.values?.wasteCollectionCompanyDeliveryAddress ?? ''
  })

  const mutateReport = async values => {
    editItem
      ? await makeCorePutRequest(
          `/waste-management-system/waste-accompanying-note/${editItem.id}`,
          values
        )
      : await makeCorePostRequest('/waste-management-system/waste-accompanying-note', values)
  }

  const { isLoading, mutate } = useMutation(mutateReport, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Avizul a fost ${editItem?.id ? 'editat' : 'creat'} cu succes.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `A aparut o eroare la ${editItem?.id ? 'editarea avizului.' : 'adaugarea avizului.'}`,
        showNotification
      )
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Editeaza aviz de insotire' : 'Adauga aviz de insotire'}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} item>
          <FormInfoItems
            items={[
              {
                label: 'Locatie:',
                name: currentStore?.storeName,
                itemStyle: { gridTemplateColumns: '60px 1fr' }
              }
            ]}
          />
        </Grid>
        {isEdit && (
          <Grid xs={12} item>
            <Alert severity="warning">
              Destinatarul nu se poate modifica. Pentru a selecta un destinatar diferit trebuie
              generat alt aviz.
            </Alert>
          </Grid>
        )}
        <Grid xs={12} md={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="company"
            label="Destinatar"
            value={formik.values.company}
            options={companyOptions || []}
            onChange={value => {
              formik.setFieldValue('company', value)
              formik.setFieldValue('wasteReports', [])
            }}
            error={!!formik.errors.company}
            helperText={formik.errors.company?.value as string}
            required
            disabled={isEdit}
          />
        </Grid>
        <Grid xs={12} md={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="wasteReports"
            label="Anexe transport"
            value={formik.values.wasteReports}
            options={wasteReportsOptions || []}
            onChange={value => formik.setFieldValue('wasteReports', value)}
            error={!!formik.errors.wasteReports}
            helperText={formik.errors.wasteReports as string}
            required
            multiple
            disabled={isEdit}
          />
        </Grid>
        <Grid xs={12} md={6} item className={ls.disabledInputContainer}>
          <Input
            type="text"
            name="totalWeight"
            label="Greutate totala (kg)"
            fullWidth
            variant="outlined"
            value={totalWeight}
            disabled
          />
        </Grid>
        <Grid item xs={12} className={ls.title}>
          Adresa livrare
        </Grid>
        <Grid xs={12} md={12} item>
          <Autocomplete
            {...wasteCollectionCompanyDeliveryAddressSelectSearch}
            label="Adresa livrare"
            variant="outlined"
            freeSolo
            autoComplete={false}
            filterOptions={x => x}
            value={formik.values.wasteCollectionCompanyDeliveryAddress}
            onChange={(event, value) => {
              if (value?.address) {
                formik.setFieldValue('wasteCollectionCompanyDeliveryAddress', value.address)
                formik.setFieldValue('wasteCollectionCompanyDeliveryAddressId', value.id)
              } else {
                formik.setFieldValue('wasteCollectionCompanyDeliveryAddress', value)
                formik.setFieldValue('wasteCollectionCompanyDeliveryAddressId', '')
              }
            }}
            onClose={event => {
              if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                formik.setFieldValue(
                  'wasteCollectionCompanyDeliveryAddress',
                  (event.target as HTMLInputElement).value
                )
              }
            }}
            error={!!formik.errors.wasteCollectionCompanyDeliveryAddress}
            helperText={formik.errors.wasteCollectionCompanyDeliveryAddress as string}
            required
          />
        </Grid>
        <Grid item xs={12} className={ls.title}>
          Delegat
        </Grid>
        <Grid xs={12} md={6} item>
          <Autocomplete
            {...delegateSelectSearch}
            label="Nume delegat"
            variant="outlined"
            freeSolo
            autoComplete={false}
            filterOptions={x => x}
            value={formik.values.delegateName}
            onChange={(event, value) => {
              if (value?.name) {
                formik.setFieldValue('delegateId', value.id)
                formik.setFieldValue('delegateName', value.name)
                formik.setFieldValue('cnp', value.cnp)
                formik.setFieldValue('identityCardSeries', value.identityCardSeries)
                formik.setFieldValue('identityCardNumber', value.identityCardNumber)
                formik.setFieldValue('identityCardIssuer', value.identityCardIssuer)
                formik.setFieldValue('vehicleType', value.vehicleType)
                formik.setFieldValue('vehicleNumber', value.vehicleNumber)
              } else {
                formik.setFieldValue('delegateId', '')
                formik.setFieldValue('delegateName', value)
                formik.setFieldValue('cnp', '')
                formik.setFieldValue('identityCardSeries', '')
                formik.setFieldValue('identityCardNumber', '')
                formik.setFieldValue('identityCardIssuer', '')
                formik.setFieldValue('vehicleType', '')
                formik.setFieldValue('vehicleNumber', '')
              }
            }}
            onClose={event => {
              if ((event.target as HTMLInputElement).tagName.toUpperCase() === 'INPUT') {
                formik.setFieldValue('delegateName', (event.target as HTMLInputElement).value)
              }
            }}
            error={!!formik.errors.delegateName}
            helperText={formik.errors.delegateName as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="cnp"
            label="CNP delegat"
            fullWidth
            variant="outlined"
            value={formik.values.cnp}
            onChange={event => formik.setFieldValue('cnp', event.currentTarget.value)}
            error={!!formik.errors.cnp}
            helperText={formik.errors.cnp as string}
          />
        </Grid>
        <Grid xs={6} md={3} item>
          <Input
            type="text"
            name="identityCardSeries"
            label="Serie CI"
            fullWidth
            variant="outlined"
            value={formik.values.identityCardSeries}
            onChange={event =>
              formik.setFieldValue('identityCardSeries', event.currentTarget.value)
            }
            error={!!formik.errors.identityCardSeries}
            helperText={formik.errors.identityCardSeries as string}
          />
        </Grid>
        <Grid xs={6} md={3} item>
          <Input
            type="text"
            name="identityCardNumber"
            label="Nr CI"
            fullWidth
            variant="outlined"
            value={formik.values.identityCardNumber}
            onChange={event =>
              formik.setFieldValue('identityCardNumber', event.currentTarget.value)
            }
            error={!!formik.errors.identityCardNumber}
            helperText={formik.errors.identityCardNumber as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="identityCardIssuer"
            label="Eliberat de"
            fullWidth
            variant="outlined"
            value={formik.values.identityCardIssuer}
            onChange={event =>
              formik.setFieldValue('identityCardIssuer', event.currentTarget.value)
            }
            error={!!formik.errors.identityCardIssuer}
            helperText={formik.errors.identityCardIssuer as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="vehicleType"
            label="Mijloc de transport"
            fullWidth
            variant="outlined"
            value={formik.values.vehicleType}
            onChange={event => formik.setFieldValue('vehicleType', event.currentTarget.value)}
            error={!!formik.errors.vehicleType}
            helperText={formik.errors.vehicleType as string}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="vehicleNumber"
            label="Nr mijloc de transport"
            fullWidth
            variant="outlined"
            value={formik.values.vehicleNumber}
            onChange={event => formik.setFieldValue('vehicleNumber', event.currentTarget.value)}
            error={!!formik.errors.vehicleNumber}
            helperText={formik.errors.vehicleNumber as string}
          />
        </Grid>
        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Salveaza"
            onClick={() => formik.handleSubmit()}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default AccompanyingNoteModal
